import React from 'react';
import { Pencil, Trash2, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { findAssetType, missingKeyFields } from './assetTypes';

const formatMoney = (value, currency) => {
  if (value == null || !Number.isFinite(value)) return null;
  try {
    return new Intl.NumberFormat('en-US', {
      style: currency ? 'currency' : 'decimal',
      currency: currency || undefined,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toLocaleString()} ${currency || ''}`.trim();
  }
};

const ContinuityAssetCard = ({ asset, onEdit, onDelete }) => {
  const meta = findAssetType(asset.asset_type);
  if (!meta) return null;
  const Icon = meta.icon;
  const summary = meta.summarise(asset.details || {});
  const priceLabel = formatMoney(asset.estimated_value, asset.currency);
  const missing = missingKeyFields(asset, meta);

  return (
    <div
      className={`rounded-xl border ${meta.accent.ring} bg-white p-4 shadow-xs hover:shadow-md transition`}
      data-testid={`continuity-asset-${asset.asset_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 text-slate-600 shrink-0" />
          <div className="min-w-0">
            <h4 className="font-semibold text-slate-900 truncate">{asset.nickname}</h4>
            <p className="text-xs text-slate-500 truncate">{summary}</p>
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button variant="ghost" size="icon" onClick={() => onEdit(asset)} data-testid={`ca-edit-${asset.asset_id}`}>
            <Pencil className="w-4 h-4 text-slate-600" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(asset)} data-testid={`ca-delete-${asset.asset_id}`}>
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {priceLabel && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
            {priceLabel}
          </span>
        )}
        {asset.include_in_net_worth && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
            In Net Worth
          </span>
        )}
        <span className={`text-xs px-2 py-0.5 rounded-full ${meta.accent.badge}`}>
          {meta.label}
        </span>
      </div>

      {/* Nudge on empty fields — one amber chip per missing key field.
          Each chip opens the edit modal so the founder can fill it in.
          Kept quiet (amber, small) so a complete card stays visually clean. */}
      {missing.length > 0 && (
        <div
          className="mt-3 pt-3 border-t border-amber-100 flex flex-wrap items-center gap-1.5"
          data-testid={`ca-nudge-row-${asset.asset_id}`}
        >
          <span className="text-[11px] text-amber-800 font-medium mr-1">Still to add:</span>
          <span
            className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 font-semibold min-w-[18px] text-center leading-none"
            data-testid={`ca-nudge-count-${asset.asset_id}`}
            aria-label={`${missing.length} field${missing.length === 1 ? '' : 's'} still to add`}
          >
            {missing.length}
          </span>
          {missing.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => onEdit(asset)}
              data-testid={`ca-nudge-${asset.asset_id}-${f.key}`}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 transition"
            >
              <PenLine className="w-3 h-3" />
              <span>Add {f.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContinuityAssetCard;
